import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { useNotifications } from '@/modules/notifications/hooks/useNotifications';
import { getOnboardingCategories } from '@/modules/onboarding/domain/resources/core/getOnboardingCategories';
import { getOnboardingJobCategories } from '@/modules/onboarding/domain/resources/core/getOnboardingJobCategories';
import { UserEntity } from '@/modules/user/domain/entities/UserEntity';
import { updateUser } from '@/modules/user/domain/resources';
import { User } from '@/types';
import { Langs } from '@/types/lang';

export const usePersonalization = () => {
  const { locale } = useIntl();
  const { showNotificationError } = useNotifications();
  const queryClient = useQueryClient();

  const { data: categories, isLoading: isLoadingCategories } = useQuery(
    ['onboarding', 'categories', locale],
    () => getOnboardingCategories(Langs.Fr),
    {
      onError: () => {
        showNotificationError('notification.error.onboardingCategories');
      },
    }
  );

  const { data: jobCategories, isLoading: isLoadingJobCategories } = useQuery(
    ['onboarding', 'jobCategories', locale],
    () => getOnboardingJobCategories(),
    {
      onError: () => {
        showNotificationError('notification.error.onboardingCategories');
      },
    }
  );

  const { mutateAsync: updateUserAsync } = useMutation(
    ({ userId, data }: { userId: string; data: Partial<User> }) => updateUser(userId, data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['homepage']);
        const userEntity = new UserEntity(data);
        queryClient.setQueryData(['user'], userEntity);
      },
      onError: () => {
        showNotificationError('notification.error.onboardingCategories');
      },
    }
  );

  return {
    categories: categories,
    jobCategories: jobCategories || [{ items: [] }],
    isLoadingCategories,
    isLoadingJobCategories,
    updateUserAsync,
  };
};
